import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, QrCode, CheckCircle2, AlertCircle, Webhook, PowerOff } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { notify } from '../../lib/notify';

interface GatewaySession {
  available: boolean;
  message?: string;
  sessionId?: string;
  status?: string | null;
  jid?: string | null;
  name?: string | null;
}

const STATUS_LABEL: Record<string, { label: string; tone: 'ok' | 'warn' | 'err' }> = {
  // Estados de OpenWA v0.1.6 (verificados contra entities/session.entity.ts).
  created: { label: 'Creada (sin iniciar)', tone: 'warn' },
  initializing: { label: 'Iniciando…', tone: 'warn' },
  qr_ready: { label: 'Esperando escaneo de QR', tone: 'warn' },
  authenticating: { label: 'Autenticando…', tone: 'warn' },
  ready: { label: 'Conectado', tone: 'ok' },
  disconnected: { label: 'Desconectado', tone: 'err' },
  failed: { label: 'Falló', tone: 'err' },
};

export default function WhatsappBot() {
  const [session, setSession] = useState<GatewaySession | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [qr, setQr] = useState<string | null>(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [startingSession, setStartingSession] = useState(false);
  const [settingWebhook, setSettingWebhook] = useState(false);

  const refreshSession = async () => {
    setLoadingSession(true);
    try {
      // apiFetch auto-unwrappea el `data` del envelope { data: ... } del API.
      // El tipo genérico es lo que viene DENTRO de `data`, no el envelope.
      const data = await apiFetch<GatewaySession>(`/admin/whatsapp/gateway/session`);
      setSession(data);
    } catch (err) {
      notify.apiError(err, 'No se pudo consultar el estado del bot.');
    } finally {
      setLoadingSession(false);
    }
  };

  const refreshQr = async (silent = false) => {
    if (!silent) setLoadingQr(true);
    try {
      const data = await apiFetch<{ qr: string | null; status: string | null }>(
        `/admin/whatsapp/gateway/qr`,
      );
      const qrValue = data?.qr ?? null;
      setQr(qrValue);
      if (!silent && !qrValue) {
        notify.info('Sin QR disponible.', 'El gateway todavía no tiene un QR — espera unos segundos y refrescá.');
      }
      await refreshSession();
    } catch (err) {
      if (!silent) notify.apiError(err, 'No se pudo obtener el QR.');
    } finally {
      if (!silent) setLoadingQr(false);
    }
  };

  const startSession = async () => {
    setStartingSession(true);
    try {
      await apiFetch(`/admin/whatsapp/gateway/session/start`, { method: 'POST' });
      notify.success('Sesión iniciada.', 'Si es la primera vez, abre el QR para vincular el bot.');
      setTimeout(refreshSession, 1500);
    } catch (err) {
      notify.apiError(err, 'No se pudo iniciar la sesión.');
    } finally {
      setStartingSession(false);
    }
  };

  const setupWebhook = async () => {
    setSettingWebhook(true);
    try {
      const data = await apiFetch<{ url: string }>(`/admin/whatsapp/gateway/webhook/setup`, {
        method: 'POST',
      });
      notify.success('Webhook registrado.', data.url);
    } catch (err) {
      notify.apiError(err, 'No se pudo registrar el webhook.');
    } finally {
      setSettingWebhook(false);
    }
  };

  useEffect(() => {
    void refreshSession();
  }, []);

  // Auto-refresh del status del bot cada 5s mientras esté en estados
  // transitorios (initializing/qr_ready/authenticating). Una vez ready o
  // failed, paramos para no martillar al gateway sin necesidad.
  useEffect(() => {
    const s = session?.status ?? '';
    const transitional = s === 'initializing' || s === 'qr_ready' || s === 'authenticating';
    if (!transitional) return;
    const t = setInterval(refreshSession, 5_000);
    return () => clearInterval(t);
  }, [session?.status]);

  // Cuando entramos a `qr_ready` cargamos el QR automáticamente sin que el
  // usuario tenga que tocar nada — y lo refrescamos cada 25s porque
  // WhatsApp invalida los QR a los ~30-60s.
  useEffect(() => {
    if (session?.status !== 'qr_ready') {
      setQr(null);
      return;
    }
    void refreshQr(true);
    const t = setInterval(() => void refreshQr(true), 25_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  if (loadingSession && !session) {
    return (
      <div className="py-12 flex items-center justify-center text-slate-500">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  if (session && !session.available) {
    return (
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-600 mt-0.5 shrink-0" size={20} />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-amber-900">Gateway no disponible</h3>
              <p className="text-sm text-amber-800 mt-1">{session.message}</p>
              <p className="text-xs text-amber-700 mt-3">
                Seguí las instrucciones en{' '}
                <code className="px-1 py-0.5 bg-amber-100 rounded">
                  docs/integraciones/WHATSAPP-OPENWA.md
                </code>{' '}
                para instalar el gateway y configurar las variables de entorno.
              </p>
            </div>
            <button
              onClick={refreshSession}
              className="px-2 py-1 text-xs rounded-md bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusKey = (session?.status ?? '').toString();
  const statusMeta = STATUS_LABEL[statusKey] ?? {
    label: statusKey || 'Desconocido',
    tone: 'warn' as const,
  };
  const isConnected = statusMeta.tone === 'ok';

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Estado del bot</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Sesión <code className="text-xs">{session?.sessionId}</code> en el gateway Open-WA.
            </p>
          </div>
          <button
            onClick={refreshSession}
            disabled={loadingSession}
            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md disabled:opacity-50"
            title="Refrescar"
          >
            <RefreshCw size={14} className={loadingSession ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat
            label="Estado"
            value={statusMeta.label}
            tone={statusMeta.tone}
            icon={isConnected ? CheckCircle2 : statusMeta.tone === 'err' ? PowerOff : AlertCircle}
          />
          <Stat
            label="Cuenta vinculada"
            value={session?.name || session?.jid || '—'}
            tone={session?.jid ? 'ok' : 'warn'}
          />
          <Stat
            label="JID"
            value={session?.jid || '—'}
            tone="muted"
            mono
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {(session?.status === 'created' || session?.status === 'disconnected' || session?.status === 'failed') && (
            <button
              onClick={startSession}
              disabled={startingSession}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition disabled:opacity-50"
            >
              {startingSession ? <Loader2 size={14} className="animate-spin" /> : null}
              Iniciar / Reanudar sesión
            </button>
          )}
          {session?.status === 'qr_ready' && (
            <button
              onClick={() => void refreshQr(false)}
              disabled={loadingQr}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
              title="Pedir un QR nuevo al gateway"
            >
              {loadingQr ? <Loader2 size={14} className="animate-spin" /> : <QrCode size={14} />}
              Refrescar QR ahora
            </button>
          )}
          <button
            onClick={setupWebhook}
            disabled={settingWebhook || !isConnected}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
            title={isConnected ? 'Registrar el webhook contra el gateway' : 'Disponible cuando la sesión esté conectada'}
          >
            {settingWebhook ? <Loader2 size={14} className="animate-spin" /> : <Webhook size={14} />}
            Registrar webhook
          </button>
        </div>
      </div>

      {qr && !isConnected && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-900">Escaneá el QR con WhatsApp</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Desde el teléfono del bot: <strong>Configuración → Dispositivos vinculados → Vincular dispositivo</strong>.
            El QR se renueva cada minuto.
          </p>
          <div className="mt-4 flex items-center justify-center bg-white p-4 rounded-lg border border-slate-100">
            {qr.startsWith('data:image') ? (
              <img src={qr} alt="QR del bot" className="max-w-[280px] h-auto" />
            ) : (
              <img
                src={`data:image/png;base64,${qr.replace(/^base64,/, '')}`}
                alt="QR del bot"
                className="max-w-[280px] h-auto"
              />
            )}
          </div>
        </div>
      )}

      {isConnected && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900 flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <div>
            Bot conectado. Ya puedes registrar grupos desde la pestaña <strong>Grupos</strong>{' '}
            y los mensajes mencionando al bot van a crear tickets automáticamente.
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'muted',
  mono = false,
  icon: Icon,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'err' | 'muted';
  mono?: boolean;
  icon?: typeof CheckCircle2;
}) {
  const toneClass = {
    ok: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    warn: 'text-amber-700 bg-amber-50 border-amber-200',
    err: 'text-rose-700 bg-rose-50 border-rose-200',
    muted: 'text-slate-700 bg-slate-50 border-slate-200',
  }[tone];
  return (
    <div className={`px-3 py-2.5 rounded-lg border ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className={`mt-0.5 text-sm font-medium flex items-center gap-1.5 ${mono ? 'font-mono text-xs' : ''}`}>
        {Icon && <Icon size={14} />}
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}
